import React, { Fragment, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAppSelector } from '@/store/hooks';
import { getActiveTable } from '@/store/reducers/table-slice';
import { Box, Divider, Stack } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';

import { ColumnDef, DataTable } from '@/components/core/data-table';

import { FormattedDateTime } from './formatted-date';
import ItemsNotFound from './items-not-found';

// Implementation Example:
// import { AlienDomain } from '@/types/general';
// import { ColumnDef } from '@/components/core/data-table';
// const columns = [
//   {
//     field: 'domainName',
//     name: 'Domain',
//     width: '200px',
//   },
// ] satisfies ColumnDef<AlienDomain>[];
// const initialData: AlienDomain[] = []; // Fetch or preload initial data
// const initialSearchParams = new URLSearchParams({ page: '1', limit: '50' });

// const fetchDomains = async (params: URLSearchParams) => {
//   const response = await fetch(`/api/devops/domains/list?${params.toString()}`, {
//     method: 'POST',
//     body: JSON.stringify(initialSearchParams),
//   });
//   return response.json();
// };
// <ReusableTable
//       columns={columns}
//       fetchData={fetchDomains}
//       initialData={initialData}
//       initialSearchParams={initialSearchParams}
// />

export interface ReusableTableProps<T> {
  columns: ColumnDef<T>[];
  fetchData: (params: URLSearchParams) => Promise<{ data: T[]; count: number; remaining: number }>;
  initialSearchParams: URLSearchParams;
  initialData?: T[];
  pageSize?: number;
  loadingComponent?: React.ReactNode;
  noDataComponent?: React.ReactNode;
  hideHead?: boolean;
  minHeight?: number | string;
  hidePagination?: boolean;
  initialLoad?: boolean;
}

export function ReusableTable<T extends object>({
  columns,
  fetchData,
  initialData,
  initialSearchParams,
  pageSize = 50,
  // loadingComponent = (
  //   <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
  //     <CircularProgress color="primary" size={38} />
  //   </Box>
  // ),
  // <Box sx={{ p: 3 }}>
  //   <Typography color="text.secondary" sx={{ textAlign: 'center' }} variant="body2">
  //     No data found
  //   </Typography>
  // </Box>
  noDataComponent = <ItemsNotFound />,
  hideHead,
  minHeight,
  hidePagination = false,
  initialLoad = true,
}: ReusableTableProps<T>): React.JSX.Element {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [lastFetchTime, setLastFetchTime] = useState<Date | undefined>();

  const [data, setData] = useState<T[]>([]);
  // const [remaining, setRemaining] = useState(0);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(Number(initialSearchParams.get('page') || 1));
  const activeTable = useAppSelector(getActiveTable);
  const updateUrl = (page: number): void => {
    const updatedSearchParams = new URLSearchParams(Array.from(searchParams.entries()));
    updatedSearchParams.set('page', page.toString());
    updatedSearchParams.set('limit', pageSize.toString());
    router.push(`${pathname}?${updatedSearchParams.toString()}`);
  };

  const loadData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // const { data: fetchedData, remaining: fetchedRemaining, count: fetchedCount } = await fetchData(searchParams);
      const { data: fetchedData, count: fetchedCount } = await fetchData(searchParams);
      setData(fetchedData);
      // setRemaining(fetchedRemaining);
      setLastFetchTime(new Date());
      setCount(fetchedCount);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialLoad && initialData) {
      return;
    }
    void loadData();
  }, [searchParams, activeTable.refresh]);

  return (
    <Stack direction="column" gap={0.5}>
      <FormattedDateTime timestamp={lastFetchTime} variant="horizontal" />
      <Box sx={{ height: { xs: 'auto', lg: minHeight ? minHeight : 'calc(64vh) !important' }, overflow: 'auto' }}>
        {data?.length ? (
          <Box
            sx={{
              height: '100%',
              '& .MuiDataTable-root': {
                maxHeight: '100%',
                overflow: 'auto',
              },
              '& .MuiTableHead-root': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'primary.paper',
              },
            }}
          >
            <DataTable<T>
              columns={columns}
              rows={data}
              hideHead={hideHead}
              isLoading={isLoading}
              sx={{
                '::-webkit-scrollbar': {
                  display: 'block !important',
                  width: '8px ',
                  height: '8px',
                  paddingRight: '8px',
                  backgroundColor: 'transparent !important',
                },
              }}
            />
          </Box>
        ) : (
          noDataComponent
        )}
      </Box>
      {!hidePagination ? (
        <Fragment>
          <Divider />
          <TablePagination
            component="div"
            count={count}
            onPageChange={(_, page) => {
              setCurrentPage(page + 1);
              updateUrl(page + 1);
            }}
            page={currentPage - 1}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
          />
        </Fragment>
      ) : null}
    </Stack>
  );
}
